import React from 'react';
import type { User, BusinessUser } from '../../../../types/usersDemo';
import { HeadingSmall } from 'baseui/typography';
import { getStatusColor } from '../../utils/helpers';
import { useDrawerRequests } from '../../../../hooks/useDrawerData';

interface PropertiesTabProps {
  contact: User | BusinessUser;
  editingId: number | null;
  setEditingId: (id: number | null) => void;
  setPendingChange: (c: { id: number; newStatus: string; type: 'request' }) => void;
}

export const PropertiesTab: React.FC<PropertiesTabProps> = ({
  contact,
  editingId,
  setEditingId,
  setPendingChange,
}) => {
  const { requests, loading, error } = useDrawerRequests(contact);
  const isPrimaryProperty = (contactEstate?: string, requestEstate?: string) => {
    if (!contactEstate || !requestEstate) return false;
    const unitFromContact = (contactEstate.match(/Unit\s*\d+/i) || [])[0]?.toLowerCase();
    const unitFromRequest = (requestEstate.match(/Unit\s*\d+/i) || [])[0]?.toLowerCase();
    return Boolean(unitFromContact && unitFromRequest && unitFromContact === unitFromRequest);
  };

  const getRoleColor = (role: string): string => {
    const r = (role || '').toLowerCase();
    if (r.includes('guest')) return '#2D7738'; // green
    if (r.includes('supplier')) return '#1D4ED8'; // blue
    if (r.includes('owner')) return '#1D4ED8'; // blue (owners)
    if (r.includes('resident')) return '#2D7738'; // green (residents)
    if (r.includes('property')) return '#6B21A8'; // purple (property admin)
    if (r.includes('employee')) return '#B7791F'; // amber
    return '#666';
  };
  return (
    <div>
      <HeadingSmall style={{ margin: '0 0 16px 0', color: '#666' }}>Properties</HeadingSmall>
      <div style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>#</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Role</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Estate</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Property</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Date Requested</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>SLA</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request, index) => (
              <tr key={request.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                <td style={{ padding: '8px' }}>{index + 1}</td>
                <td style={{ padding: '8px', textTransform: 'capitalize' }}>
                  <span
                    style={{
                      fontSize: '12px',
                      color: getRoleColor(request.role),
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}
                  >
                    {request.role}
                  </span>
                </td>
                <td style={{ padding: '8px' }}>{request.estateGroup}</td>
                <td style={{ padding: '8px', fontWeight: '500' }}>
                  {request.estate}
                  {isPrimaryProperty((contact as User).estate, request.estate) && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      borderRadius: '12px',
                      backgroundColor: '#E8F5E8',
                      color: '#2D7738',
                      fontSize: '11px',
                      fontWeight: 500
                    }}>
                      Primary
                    </span>
                  )}
                </td>
                <td style={{ padding: '8px' }}>{request.dateRequested}</td>
                <td style={{ padding: '8px' }}>{request.sla}</td>
                <td style={{ padding: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <div 
                      role="button"
                      tabIndex={0}
                      aria-label={`Change status, current ${request.status}`}
                      aria-haspopup="listbox"
                      aria-expanded={editingId === request.id}
                      aria-controls={editingId === request.id ? `prop-status-menu-${request.id}` : undefined}
                      onClick={() => setEditingId(editingId === request.id ? null : request.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingId(editingId === request.id ? null : request.id); } }}
                      style={{ 
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: getStatusColor(request.status) === 'positive' ? '#E8F5E8' : 
                                       getStatusColor(request.status) === 'warning' ? '#FFF4E6' : 
                                       getStatusColor(request.status) === 'negative' ? '#FDE8E8' : '#F0F0F0',
                        color: getStatusColor(request.status) === 'positive' ? '#2D7738' : 
                               getStatusColor(request.status) === 'warning' ? '#B7791F' : 
                               getStatusColor(request.status) === 'negative' ? '#C53030' : '#666',
                        fontSize: '12px',
                        fontWeight: '500',
                        textAlign: 'center',
                        minWidth: '60px'
                      }}
                    >
                      {request.status}
                    </div>

                    {editingId === request.id && (
                      <div 
                        role="listbox"
                        id={`prop-status-menu-${request.id}`}
                        data-status-dropdown
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          backgroundColor: 'white',
                          border: '1px solid #e9ecef',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 1000,
                          marginTop: '2px'
                        }}
                      >
                        {['Pending', 'Approved', 'Rejected'].filter(s => s !== request.status).map((status) => (
                          <div
                            role="option"
                            aria-selected={false}
                            key={status}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: 'transparent',
                              color: '#333',
                              userSelect: 'none'
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPendingChange({ id: request.id, newStatus: status, type: 'request' });
                              setEditingId(null);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {status}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PropertiesTab;


